import { useEffect, useState, useCallback } from "react";
import {
    ChevronLeft,
    ChevronRight,
    X,
    Maximize2,
    LayoutGrid,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const SLIDE_IDS = [
    "top",
    "problem",
    "solution",
    "product",
    "metrics",
    "dashboard",
    "pricing",
    "competitive",
    "traction",
    "team",
    "cta",
];

export default function PresenterControls({ active, onExit }) {
    const { t } = useI18n();
    const slideLabels = t.presenter.slides;
    const [idx, setIdx] = useState(0);
    const [agendaOpen, setAgendaOpen] = useState(false);

    const go = useCallback((i) => {
        const next = Math.max(0, Math.min(SLIDE_IDS.length - 1, i));
        setIdx(next);
        const el = document.getElementById(SLIDE_IDS[next]);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    useEffect(() => {
        if (!active) return;
        document.body.classList.add("present-mode");
        const el = document.getElementById(SLIDE_IDS[idx]);
        if (el) el.scrollIntoView({ behavior: "instant", block: "start" });
        return () => document.body.classList.remove("present-mode");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(() => {
        if (!active) return;
        const onKey = (e) => {
            if (agendaOpen) {
                if (e.key === "Escape") {
                    e.preventDefault();
                    setAgendaOpen(false);
                }
                return;
            }
            if (
                e.key === "ArrowRight" ||
                e.key === " " ||
                e.key === "PageDown" ||
                e.key === "ArrowDown"
            ) {
                e.preventDefault();
                go(idx + 1);
            } else if (
                e.key === "ArrowLeft" ||
                e.key === "PageUp" ||
                e.key === "ArrowUp"
            ) {
                e.preventDefault();
                go(idx - 1);
            } else if (e.key === "Escape") {
                onExit();
            } else if (e.key === "Home") {
                go(0);
            } else if (e.key === "End") {
                go(SLIDE_IDS.length - 1);
            } else if (e.key === "f" || e.key === "F") {
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                } else {
                    document.documentElement.requestFullscreen?.();
                }
            } else if (e.key === "g" || e.key === "G") {
                e.preventDefault();
                setAgendaOpen((v) => !v);
            } else if (/^[1-9]$/.test(e.key)) {
                const n = parseInt(e.key, 10) - 1;
                if (n < SLIDE_IDS.length) go(n);
            } else if (e.key === "0") {
                if (SLIDE_IDS.length >= 10) go(9);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, idx, go, onExit, agendaOpen]);

    if (!active) return null;

    return (
        <>
            {/* Top-left slide name */}
            <div
                data-testid="presenter-slide-name"
                className="fixed top-5 left-5 z-[60] px-3 py-1.5 rounded-md bg-[#04140D]/80 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4FF00]"
            >
                {slideLabels[idx]}
            </div>

            {/* Top-right buttons */}
            <button
                type="button"
                onClick={() => setAgendaOpen((v) => !v)}
                data-testid="presenter-agenda-toggle"
                className="fixed top-5 right-[180px] z-[60] inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#04140D]/80 backdrop-blur-md border border-white/10 text-white/85 hover:border-[#D4FF00] hover:text-[#D4FF00] transition-colors text-xs font-bold uppercase tracking-widest"
                title={t.presenter.agendaHeader}
            >
                <LayoutGrid size={14} />
                <span className="hidden sm:inline">{t.presenter.agendaHeader}</span>
            </button>
            <button
                type="button"
                onClick={() => {
                    if (document.fullscreenElement) document.exitFullscreen();
                    else document.documentElement.requestFullscreen?.();
                }}
                data-testid="presenter-fullscreen"
                className="fixed top-5 right-[100px] z-[60] inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#04140D]/80 backdrop-blur-md border border-white/10 text-white/85 hover:border-[#D4FF00] hover:text-[#D4FF00] transition-colors text-xs font-bold uppercase tracking-widest"
                title="Fullscreen (F)"
            >
                <Maximize2 size={14} />
            </button>
            <button
                type="button"
                onClick={onExit}
                data-testid="presenter-exit"
                className="fixed top-5 right-5 z-[60] inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#04140D]/80 backdrop-blur-md border border-white/10 text-white hover:border-[#D4FF00] hover:text-[#D4FF00] transition-colors text-xs font-bold uppercase tracking-widest"
                title={t.presenter.exit}
            >
                <X size={14} />
                {t.presenter.exit}
            </button>

            {/* Bottom control bar */}
            <div
                data-testid="presenter-controls"
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1 rounded-full bg-[#04140D]/85 backdrop-blur-xl border border-white/10 p-1.5 shadow-[0_24px_60px_-15px_rgba(0,0,0,0.7)]"
            >
                <button
                    type="button"
                    onClick={() => go(idx - 1)}
                    data-testid="presenter-prev"
                    disabled={idx === 0}
                    className="h-9 w-9 grid place-items-center rounded-full text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous slide"
                >
                    <ChevronLeft size={18} />
                </button>

                <button
                    type="button"
                    onClick={() => setAgendaOpen(true)}
                    className="px-3 min-w-[78px] text-center hover:bg-white/5 rounded-full transition-colors"
                    title={t.presenter.agendaHeader}
                >
                    <div className="font-display font-bold tabular text-sm text-white leading-none">
                        {String(idx + 1).padStart(2, "0")}
                        <span className="text-white/40">
                            {" "}
                            / {String(SLIDE_IDS.length).padStart(2, "0")}
                        </span>
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => go(idx + 1)}
                    data-testid="presenter-next"
                    disabled={idx === SLIDE_IDS.length - 1}
                    className="h-9 w-9 grid place-items-center rounded-full bg-[#D4FF00] text-[#04140D] hover:bg-[#BDF000] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next slide"
                >
                    <ChevronRight size={18} />
                </button>
            </div>

            {/* Bottom hint */}
            <div className="fixed bottom-6 right-6 z-[60] hidden md:flex items-center gap-3 text-[10px] uppercase tracking-widest text-white/45 font-bold">
                <span className="px-2 py-1 rounded border border-white/15 bg-[#04140D]/70 font-mono">
                    ←
                </span>
                <span className="px-2 py-1 rounded border border-white/15 bg-[#04140D]/70 font-mono">
                    →
                </span>
                <span>{t.presenter.navigate}</span>
                <span className="px-2 py-1 rounded border border-white/15 bg-[#04140D]/70 font-mono">
                    G
                </span>
                <span>{t.presenter.agenda}</span>
                <span className="px-2 py-1 rounded border border-white/15 bg-[#04140D]/70 font-mono">
                    Esc
                </span>
                <span>{t.presenter.exit}</span>
            </div>

            {/* Progress bar */}
            <div className="fixed top-0 left-0 right-0 h-[3px] bg-white/5 z-[60]">
                <div
                    className="h-full bg-[#D4FF00] transition-all duration-500"
                    style={{ width: `${((idx + 1) / SLIDE_IDS.length) * 100}%` }}
                />
            </div>

            {/* Agenda panel */}
            {agendaOpen && (
                <div
                    data-testid="agenda-overlay"
                    className="fixed inset-0 z-[70] grid place-items-center bg-[#04140D]/85 backdrop-blur-md"
                    onClick={() => setAgendaOpen(false)}
                >
                    <div
                        className="rounded-2xl border border-white/10 bg-[#071B12] p-7 md:p-9 max-w-5xl w-[92%]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-7">
                            <div>
                                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-1.5">
                                    {t.presenter.agendaHeader}
                                </div>
                                <div className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight">
                                    {t.presenter.agendaTitle}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setAgendaOpen(false)}
                                data-testid="agenda-close"
                                className="h-10 w-10 grid place-items-center rounded-md border border-white/10 hover:border-[#D4FF00] hover:text-[#D4FF00] text-white/80"
                                aria-label="Close agenda"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {slideLabels.map((label, i) => {
                                const isActive = i === idx;
                                return (
                                    <button
                                        key={SLIDE_IDS[i]}
                                        type="button"
                                        data-testid={`agenda-slide-${i}`}
                                        onClick={() => {
                                            go(i);
                                            setAgendaOpen(false);
                                        }}
                                        className={`group relative rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 ${
                                            isActive
                                                ? "border-[#D4FF00] bg-[#D4FF00]/10"
                                                : "border-white/10 bg-[#04140D] hover:border-[#D4FF00]/40 hover:bg-[#0A2418]"
                                        }`}
                                    >
                                        <div className="aspect-[16/9] rounded-md bg-gradient-to-br from-[#071B12] to-[#0E2B1E] border border-white/5 mb-3 relative overflow-hidden">
                                            <div className="absolute inset-0 court-bg opacity-60" />
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <div
                                                    className={`font-display font-bold tabular text-3xl leading-none ${
                                                        isActive
                                                            ? "text-[#D4FF00]"
                                                            : "text-white/30 group-hover:text-[#D4FF00]/70"
                                                    } transition-colors`}
                                                >
                                                    {String(i + 1).padStart(2, "0")}
                                                </div>
                                            </div>
                                            {isActive && (
                                                <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#D4FF00] pulse-dot" />
                                            )}
                                        </div>
                                        <div
                                            className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${
                                                isActive ? "text-[#D4FF00]" : "text-white/40"
                                            }`}
                                        >
                                            {t.presenter.slideLabel}{" "}
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className="font-display font-bold text-sm md:text-base text-white tracking-tight">
                                            {label}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-7 pt-5 border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/45 font-bold">
                            <span>{t.presenter.agendaTip}</span>
                            <span>
                                <span className="px-1.5 py-0.5 rounded border border-white/15 text-white/70 font-mono">
                                    Esc
                                </span>{" "}
                                {t.presenter.agendaClose}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
